import axios, { AxiosError } from 'axios';

export type FormattedError = {
  title: string;
  message: string;
};

/**
 * Форматирует ошибку в объект с title и message
 * @param error - Ошибка любого типа (Error, AxiosError, строка и т.д.)
 * @returns Объект с title и message
 */
export const formatError = (error: unknown): FormattedError => {
  if (typeof error === 'string') {
    return {
      title: 'Ошибка',
      message: error,
    };
  }

  if (error instanceof Error) {
    if (axios.isAxiosError(error)) {
      return formatAxiosError(error);
    }

    return {
      title: 'Ошибка',
      message: error.message || 'Произошла неизвестная ошибка',
    };
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = String(error.message);

    return {
      title: 'Ошибка',
      message: message || 'Произошла неизвестная ошибка',
    };
  }

  return {
    title: 'Ошибка',
    message: 'Произошла неизвестная ошибка',
  };
};

/**
 * Форматирует AxiosError в объект с title и message на русском
 */
const formatAxiosError = (error: AxiosError): FormattedError => {
  if (error.code === 'ERR_CANCELED') {
    return {
      title: 'Запрос отменен',
      message: 'Запрос был отменен',
    };
  }

  if (!error.response) {
    return formatAxiosErrorWithoutResponse(error);
  }

  return formatAxiosErrorWithResponse(error);
};

/**
 * Форматирует AxiosError без response (сетевые ошибки)
 */
const formatAxiosErrorWithoutResponse = (error: AxiosError): FormattedError => {
  if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
    return {
      title: 'Сетевая ошибка',
      message: 'Не удалось подключиться к серверу. Проверьте подключение к интернету.',
    };
  }

  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    return {
      title: 'Превышено время ожидания',
      message: 'Сервер не отвечает слишком долго. Попробуйте позже.',
    };
  }

  return {
    title: 'Ошибка сети',
    message: error.message || 'Не удалось выполнить запрос',
  };
};

/**
 * Извлекает сообщение об ошибке из данных ответа сервера
 */
const extractMessageFromResponseData = (data: unknown): string => {
  if (typeof data === 'string') {
    return data;
  }

  if (data && typeof data === 'object') {
    if ('message' in data && typeof data.message === 'string') {
      return data.message;
    }

    if ('error' in data && typeof data.error === 'string') {
      return data.error;
    }

    if ('errorMessage' in data && typeof data.errorMessage === 'string') {
      return data.errorMessage;
    }

    if ('detail' in data && typeof data.detail === 'string') {
      return data.detail;
    }
  }

  return 'Произошла ошибка при выполнении запроса';
};

/**
 * Маппинг HTTP статусов на заголовки ошибок
 */
const STATUS_TITLES: Record<number, string> = {
  400: 'Неверный запрос',
  401: 'Ошибка авторизации',
  403: 'Доступ запрещен',
  404: 'Не найдено',
  409: 'Конфликт',
  422: 'Ошибка валидации',
  429: 'Слишком много запросов',
  500: 'Ошибка сервера',
  502: 'Сервер не отвечает',
  503: 'Сервис недоступен',
  504: 'Превышено время ожидания',
};

/**
 * Маппинг HTTP статусов на сообщения по умолчанию
 */
const STATUS_DEFAULT_MESSAGES: Record<number, string> = {
  400: 'Проверьте правильность введенных данных',
  401: 'Необходимо авторизоваться',
  403: 'У вас нет доступа к этому ресурсу',
  404: 'Запрашиваемый ресурс не найден',
  409: 'Произошел конфликт при выполнении операции',
  422: 'Данные не прошли валидацию',
  429: 'Превышен лимит запросов. Попробуйте позже.',
  500: 'Внутренняя ошибка сервера. Попробуйте позже.',
  502: 'Сервер временно недоступен. Попробуйте позже.',
  503: 'Сервис временно недоступен. Попробуйте позже.',
  504: 'Сервер не отвечает слишком долго. Попробуйте позже.',
};

/**
 * Форматирует AxiosError с response (ошибки с HTTP статусом)
 */
const formatAxiosErrorWithResponse = (error: AxiosError): FormattedError => {
  const status = error.response!.status;
  const statusText = error.response!.statusText;
  const data = error.response!.data;

  const message = extractMessageFromResponseData(data);
  const title = STATUS_TITLES[status] || `Ошибка ${status}`;
  const defaultMessage =
    STATUS_DEFAULT_MESSAGES[status] || statusText || 'Произошла ошибка при выполнении запроса';

  return {
    title,
    message: message || defaultMessage,
  };
};
